import { EncoderDecoder } from '@iotize/common/converter/api';
import { Tap } from '@iotize/tap';
import { DataConfig } from '@iotize/tap/config/schema/v1';
import { MemoryInfo, ModbusOptions } from '@iotize/tap/service/impl/target';
import { KeyType } from 'crypto';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import {
  createMonitoringTicker,
  MonitoringController,
} from '../monitor/monitor-engine';
import { TapDataError } from '../tap-data-error';
import { AbstractVariable } from '../target-variable/abstract-variable-stream';
import { ModbusAccessVariable } from '../target-variable/modbus/modbus-access-variable';
import {
  createTapBundleFromConfig,
  TapBundleConfig,
  TapVariableConfig,
} from '../target-variable/tap-bundle/tap-bundle';
import {
  createTapVariableFromConfig,
  TapVariable,
} from '../target-variable/tap-variable/tap-variable';
import { TargetMemoryVariable } from '../target-variable/target-memory/target-memory-variable';
import {
  BundleDataStreamInterface,
  EditableDataStreamInterface,
  KeyTypeType,
} from '../utility/editable-data-stream';
import { createDataManagerConfigFromTapDataConfig } from './create-data-manager-config-from-tap-data-config';
import {
  BundlesConfig,
  createTapBundlesFromConfig,
  DataManagerSync,
  readDataManagerTapConfigFromTap,
} from './create-data-manager-from-tap-config';

const TAG = 'DataManager';

// type ExtractLevel2Key<T> = T extends object
//     ? (
//         T[keyof T] extends object
//         ? { [K in keyof T]: ExtractLevel2Key<T[K]> }[keyof T]
//         : keyof T
//     )
//     : never;

type ExtractLevel2Key<T> = T extends object
  ? T[keyof T] extends object
    ? { [K in keyof T]: keyof T[K] }[keyof T]
    : keyof T
  : never;

type KeyOrNever<T, K> = K extends keyof T ? K : never;

type BundleKeyFromVariableType<T, K> = T[keyof T] extends object
  ? // ? KeyOrNever<T[keyof T], K>
    {
      [K2 in keyof T]: KeyOrNever<T[K2], K> extends never ? never : K2;
    }[keyof T]
  : never;

/**
 * Typed data manager is usually build from Tap configuration
 *
 * It allow you to have compile time check for bundle/variable types.
 * However, you cannot add/remove/edit variables (as it would change types)
 * Use `DataManager` if you want this features
 */
export class TypedDataManager<DataType> {
  // switchableStream!: SwitchableStream;
  // dataStream: Observable<Record<string, any>>;
  // tlvConverter: TlvBundleConverter<Record<string, Uint8Array>>;
  // sourceControllers: Record<string, DataSourceController> = {};
  // protected variablesCache: Record<VariableKey, EditableDataStream<VariableByType[VariableKey], unknown>>;

  // private tickers: Record<string, {
  //     controller: MonitoringControllerSubject,
  //     ticker: Observable<MonitoringTickerState>
  // }> = {};
  // private ticker = createMonitoringTicker(this.monitoring);

  private _events = new Subject<DataManager.Event>();

  get values(): Observable<DataType> {
    const bundles = this.listBundles();
    return combineLatest(
      bundles.map((v) => v.values.pipe(startWith(undefined))),
    ).pipe(
      map((values) => {
        return values.reduce<Partial<DataType>>((accumulator, v, index) => {
          const bundleStreamId = bundles[index].id as unknown as keyof DataType;
          accumulator[bundleStreamId] = v;
          return accumulator;
        }, {}) as DataType;
      }),
    );
  }

  get events() {
    return this._events.asObservable();
  }

  constructor(
    public tap: Tap,
    protected bundles: Record<
      keyof DataType,
      BundleDataStreamInterface<DataType[keyof DataType]>
    >,
    // variables: Record<VariableKey, EditableDataStream<VariableByType[VariableKey]>>
    public monitoring = new MonitoringController(),
  ) {
    // const variablesCache: Record<string, EditableDataStream<VariableByType[VariableKey], unknown>> = {};
    // for (const bundleKey in this.bundles) {
    //     const bundle = this.bundles[bundleKey];
    //     for (const variableKey in bundle.variables) {
    //         const variable = bundle.variables[variableKey];
    //         variablesCache[variable.config.name] = variable;
    //     }
    // }
    // this.variablesCache = variablesCache as Record<VariableKey, EditableDataStream<VariableByType[VariableKey], unknown>>;
    // this.setupStreams();

    // this.variablesCache = {} as any;

    const ticker = createMonitoringTicker(this.monitoring);

    ticker.subscribe(async (newTick) => {
      // console.log('New tick', newTick);
      await this.refreshValues();
    });
  }

  listVariables(): EditableDataStreamInterface<
    unknown,
    Uint8Array,
    KeyTypeType
  >[] {
    return this.listBundles().reduce<
      EditableDataStreamInterface<unknown, Uint8Array, KeyTypeType>[]
    >((acc, bundle) => {
      acc.push(...bundle.variables);
      return acc;
    }, []);
  }

  public bundle<T extends keyof DataType>(
    key: T,
  ): BundleDataStreamInterface<DataType[T]> {
    if (!(key in this.bundles)) {
      throw TapDataError.bundleNotFound(key);
    }
    return this.bundles[key] as unknown as BundleDataStreamInterface<
      DataType[T]
    >;
  }

  // TODO fix typing
  public variable<VK extends ExtractLevel2Key<DataType>>(
    key: VK,
  ): EditableDataStreamInterface<
    VK extends keyof DataType[BundleKeyFromVariableType<DataType, VK>]
      ? DataType[BundleKeyFromVariableType<DataType, VK>][VK]
      : never,
    Uint8Array,
    VK
  > {
    const variable = this.listVariables().find((v) => v.id === key);
    if (!variable) {
      throw TapDataError.variableNotFound(key);
    }
    return variable as any;
  }

  // public addBundleFromConfig(key: BundleKey, bundleConfig: BundleConfig) {
  //     // const key = bundleConfig.name || bundleConfig.id.toString();
  //     // this.bundles[key] = createTapBundleDataFromConfig(bundleConfig);
  //     throw new Error(`Not implemented yet`);
  //     return this;
  // }

  public destroy() {
    this.monitoring.stop();
  }

  async refreshValues() {
    const values: Partial<DataType> | Record<keyof DataType, Error> = {};
    for (const bundle of this.listBundles()) {
      values[bundle.id as unknown as keyof DataType] = await bundle
        .read()
        .catch((error) => {
          this._events.next({
            error,
            type: DataManager.EventType.error,
          });
          return error;
        });
    }
    return values;
  }

  public listBundles(): BundleDataStreamInterface<DataType[keyof DataType]>[] {
    return Object.values(this.bundles);
  }

  // public setupStreams() {
  //     // this.bundle('x').monitor.start();
  //     // this.variable('y').monitor.

  //     const variableStream = this.ticker
  //         .pipe(
  //             switchMap(() => {
  //                 return merge(
  //                     this.listVariables().map(v => defer(() => v.read()))
  //                 );
  //             })
  //         );
  //     const bundleStream = this.ticker
  //         .pipe(
  //             switchMap(() => {
  //                 return merge(
  //                     this.listBundles().map(v => defer(() => v.read()))
  //                 );
  //             })
  //         );
  //     // const profileStream = this.ticker
  //     //     .pipe(
  //     //         switchMap(async () => {
  //     //             // debug(TAG, `New call ${time} to read profile`);
  //     //             const profileData = (await this.tap.service.variable.readProfile()).body();
  //     //             const decoded = this.tlvConverter.decode(profileData);
  //     //             this.rawDataStream.next(decoded);
  //     //         })
  //     //     );
  //     this.switchableStream
  //         .addStream(
  //             'variable',
  //             variableStream
  //         )
  //         .addStream(
  //             'bundle',
  //             bundleStream
  //         )
  //     // .addStream(
  //     //     'profile',
  //     //     profileStream
  //     // );
  // }

  hasBundle<BundleKey extends KeyTypeType>(bundleKey: BundleKey) {
    return bundleKey in this.bundles;
  }

  createConfiguredBundle<DataType>(
    key: KeyTypeType,
    config: TapBundleConfig<keyof DataType>,
  ) {
    return createTapBundleFromConfig<DataType>(this.tap, key, config);
  }

  createConfiguredVariable<DataType, KeyType extends KeyTypeType>(
    key: KeyType,
    config: TapVariableConfig<DataType>,
  ): TapVariable<DataType, KeyType> {
    return createTapVariableFromConfig<DataType, KeyType>(
      this.tap.service.variable,
      key,
      config,
    );
  }

  /**
   * Create a variable with direct modbus access
   * May/May not be available according to the configured target protocol on your device
   *
   * @param id
   * @param options
   */
  createModbusAccessVariable<Key extends KeyTypeType, DataType>(
    id: Key,
    options: {
      config: ModbusOptions;
      converter: EncoderDecoder<DataType, Uint8Array>;
    },
  ) {
    return new ModbusAccessVariable(id, {
      config: options.config,
      targetService: this.tap.service.target,
      converter: options.converter,
    });
  }

  /**
   * Create direct target access to variable
   * May/May not be available according to configured target protocol on your device
   * @param id
   * @param options
   */
  createTargetMemoryVariable<Key extends KeyTypeType>(
    id: Key,
    options: {
      config: MemoryInfo;
      converter: EncoderDecoder<DataType, Uint8Array>;
    },
  ) {
    return new TargetMemoryVariable(id, {
      config: options.config,
      targetService: this.tap.service.target,
      converter: options.converter,
    });
  }
}

export class DataManager<
  DataType = Record<KeyTypeType, Record<string, any>>,
> extends TypedDataManager<DataType> {
  constructor(
    tap: Tap,
    bundles: Record<KeyTypeType, BundleDataStreamInterface<any>> = {},
    // variables: Record<VariableKey, EditableDataStream<VariableByType[VariableKey]>>
    monitoring = new MonitoringController(),
  ) {
    super(tap, bundles, monitoring);
  }

  // registerConfiguredBundle(config: BundleConfig) {
  //     throw new Error('Not implemented yet');
  // }

  // registerConfiguredVariable(config: VariableConfig) {
  //     // this.variables.push();
  //     throw new Error('Not implemented yet');
  // }

  // createTapBundle<T>(name: string, bundleId: number, variables: T): BundleDataStreamInterface<T> {
  //     return createTapBundleFromConfig(
  //         this.tap,
  //         name,
  //         {
  //             id: bundleId,
  //             variables
  //         }
  //     );
  // }

  setBundles(bundles: Record<KeyTypeType, BundleDataStreamInterface<any>>) {
    this.bundles = bundles;
  }

  addBundle<T>(bundle: BundleDataStreamInterface<T>) {
    (this.bundles as any)[bundle.id] = bundle;
  }

  removeBundle(bundleKey: KeyTypeType) {
    delete (this.bundles as any)[bundleKey];
  }

  addVariable<VariableDataType, VariableKeyType extends KeyTypeType>(
    bundleKey: keyof DataType,
    variable: AbstractVariable<VariableDataType, VariableKeyType>,
  ) {
    const bundle = this.bundle(bundleKey);
    bundle.variables.push(variable as any); // TODO fix typing
  }

  /**
   * Register bundles configured in the Tap
   */
  registerBundles<DataType>(bundlesConfig: BundlesConfig<DataType>) {
    const tapBundles = createTapBundlesFromConfig(this.tap, bundlesConfig);
    this.setBundles(tapBundles);
    return tapBundles;
  }

  registerBundle<VariableKeys extends KeyTypeType>(
    key: KeyTypeType,
    config: TapBundleConfig<VariableKeys>,
  ) {
    const tapBundle = createTapBundleFromConfig<any>(
      this.tap,
      key,
      config as any,
    );
    this.addBundle<any>(tapBundle);
  }

  clear() {
    this.bundles = {} as any;
  }

  /**
   * Synchronize DataManager bundles and variables with the connected Tap device
   * It will the current profile accessible variables/bundles
   */
  synchronizeTapConfig() {
    return new Observable<DataManager.SynchronizeEvent>((observer) => {
      try {
        readDataManagerTapConfigFromTap(this.tap, {
          observer,
        })
          .then((config) => {
            const newBundles = this.registerBundles(config);
            observer.next({
              step: 'done',
              bundles: newBundles,
            });
            observer.complete();
          })
          .catch((err) => {
            observer.error(err);
          });
      } catch (err) {
        observer.error(err);
      }
    });
  }

  /**
   * Configure DataManager bundles and variables from a DataConfig object (found in Tap configuration files)
   * Data decoder will be dedecuced from dataType/length field.
   */
  configureWithDataConfig(config: DataConfig) {
    this.clear();
    this.registerBundles(
      createDataManagerConfigFromTapDataConfig<any>(config).bundles,
    );
  }
  // createConfiguredVariable<DataType>(name: string, id: number, converter: EncoderDecoder<DataType, Uint8Array>) {
  //     return new ConfiguredVariableDataStream({
  //         tap: this.tap,
  //         config: {
  //             id,
  //             name,
  //         }
  //     }, converter);

  // }
  // addVariable<T, ContextType>(key: string, variable: VariableDataStream<ContextType, T>) {
  //     // this.additionalVariables.push()
  //     // throw new Error('Not implemented yet');
  //     this.variables[key] = variable;
  // }

  // addBundle<T, ContextType>(key: string, bundle: BundleDataStreamInterface<ContextType>) {
  //     // this.additionalVariables.push()
  //     // throw new Error('Not implemented yet');
  //     this.bundles[key] = bundle;
  // }

  // clear() {
  //     this.bundles = {};
  //     // this.variablesCache = {};
  // }
}

export namespace DataManager {
  export type SynchronizeEvent =
    | DataManagerSync.Event
    | {
        step: 'done';
        bundles: Record<KeyTypeType, TapBundleConfig<KeyTypeType>>;
      };

  export type Event = {
    type: DataManager.EventType.error;
    error: Error;
  };

  export enum EventType {
    error = 'error',
  }
}
